import { useNavigate, useSearchParams } from "react-router-dom";
import { FileText, XIcon, Plus } from "lucide-react";
import useForms from "@/features/forms/hooks/use-forms";
import { ContentLayout } from "@/components/common/content-layout";
import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import { SearchBar } from "@/components/common/search-bar";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { codeToFlag } from "@/features/regions/utils/code-to-flag";
import { Form } from "@/pages/forms/types/form";
import { PROVIDER_BADGE } from "@/features/forms/constants";

export default function Forms() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const page = parseInt(searchParams.get("page") || "0", 10);
  const status = searchParams.get("status") || "ALL";
  const search = searchParams.get("search") || "";

  const { isFetching, isLoading, data, error } = useForms(
    page,
    search.trim() || undefined,
    status !== "ALL" ? status : undefined,
  );

  const forms = data?.forms || [];
  const currentPage = data?.currentPage || 0;
  const lastPage = data ? data.lastPage + 1 : 0;

  const handlePageChange = (next: number) => {
    if (next >= 0 && next < lastPage) {
      const params = new URLSearchParams(searchParams);
      params.set("page", next.toString());
      navigate(`?${params.toString()}`, { replace: true });
    }
  };

  const handleStatusChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value === "ALL") params.delete("status");
    else params.set("status", value);
    params.set("page", "0");
    setSearchParams(params);
  };

  return (
    <ContentLayout
      header={
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All statuses</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="INACTIVE">Inactive</SelectItem>
            </SelectContent>
          </Select>
          {/* Search sits at the right edge, filters stay left. */}
          <div className="ml-auto flex-1 min-w-[280px] max-w-[420px]">
            <SearchBar
              placeholder="Search name or source_id…"
              urlParamStrategy="prefix"
              clearButton={{
                enabled: true,
                position: "inside",
                ariaLabel: "Clear search",
                icon: <XIcon className="w-4 h-4" />,
              }}
            />
          </div>
          <HasAccess permission="write">
            <Button className="bg-sidebar" onClick={() => navigate("new")}>
              <Plus className="h-4 w-4 mr-1" /> New Form
            </Button>
          </HasAccess>
        </div>
      }
      data={forms}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      errorMessage="Failed to load forms"
      emptyState={{
        message: "No forms found",
        icon: <FileText className="h-8 w-8 text-muted-foreground" />,
      }}
    >
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500">
              <th className="py-2 px-4">Name</th>
              <th className="py-2 px-4">Provider</th>
              <th className="py-2 px-4">Region</th>
              <th className="py-2 px-4">Department</th>
              <th className="py-2 px-4">Status</th>
              <th className="py-2 px-4">Bundles</th>
              <th className="py-2 px-4 w-[40px]" />
            </tr>
          </thead>
          <tbody>
            {forms.map((form: Form) => (
              <tr
                key={form.id}
                onClick={() => navigate(form.id)}
                className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
              >
                <td className="py-3 px-4">
                  <p className="text-sm font-medium text-gray-900">
                    {form.name}
                  </p>
                  <p className="text-[11px] font-mono text-gray-400">
                    {form.source_id}
                  </p>
                </td>
                <td className="py-3 px-4">
                  <Badge variant="outline" className="text-[10px]">
                    {PROVIDER_BADGE[form.provider]}
                  </Badge>
                </td>
                <td className="py-3 px-4 text-sm">
                  {form.region_rel
                    ? `${codeToFlag(form.region_rel.code)} ${form.region_rel.code.toUpperCase()}`
                    : "—"}
                </td>
                <td className="py-3 px-4 text-sm text-gray-700">
                  {form.department}
                </td>
                <td className="py-3 px-4">
                  <Badge
                    variant={form.status === "ACTIVE" ? "active" : "inactive"}
                    className="text-[10px] uppercase"
                  >
                    {form.status}
                  </Badge>
                </td>
                <td className="py-3 px-4 text-sm text-gray-700">
                  {form.bundles?.length ?? 0}
                </td>
                <td className="py-3 px-4 text-right text-gray-400">›</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {forms.length > 0 && (
        <div className="mt-4">
          <CustomPaginationComponent
            currentPage={currentPage}
            lastPage={lastPage}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </ContentLayout>
  );
}
